import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import Modal from '../layouts/Modal';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import photoAlbumService from '../../services/photoAlbumService';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';

interface CreatePhotoAlbumModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export default function CreatePhotoAlbumModal({ open, setOpen, onSuccess }: CreatePhotoAlbumModalProps) {
  const { t } = useTranslation();
  const selectedCommunity = useSelectedCommunity();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [openToUpload, setOpenToUpload] = useState(true);
  const [coverPhoto, setCoverPhoto] = useState<File | undefined>(undefined);
  const [coverPreview, setCoverPreview] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setOpenToUpload(true);
      setCoverPhoto(undefined);
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
        setCoverPreview(undefined);
      }
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  const formatTitleToId = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('page.createPhotoAlbum.images.invalidImageType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('page.createPhotoAlbum.images.imageTooLarge'));
      return;
    }

    try {
      const { resizeImage } = await import('../../utils/formatUtils');
      const resized = await resizeImage(file, 768);
      setCoverPhoto(resized);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview(URL.createObjectURL(resized));
    } catch (err) {
      console.error('Error processing image:', err);
      toast.error(t('page.createPhotoAlbum.images.imageProcessingError'));
    }
  };

  const handleRemoveImage = () => {
    setCoverPhoto(undefined);
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
      setCoverPreview(undefined);
    }
  };

  const validateStep0 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = t('page.createPhotoAlbum.errors.titleRequired');
    if (title && (title.length < 3 || title.length > 50))
      newErrors.title = t('page.createPhotoAlbum.errors.titleLength');
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleCreate = async () => {
    if (!selectedCommunity?.id) {
      toast.error(t('page.createPhotoAlbum.errors.noCommunity'));
      return;
    }

    if (!validateStep0()) {
      return;
    }

    setLoading(true);
    try {
      const albumId = formatTitleToId(title);
      const payload = {
        id: albumId,
        title: title.trim(),
        description: description.trim(),
        communityId: selectedCommunity.id,
        openToUpload,
        coverPhoto,
      } as any;

      await photoAlbumService.createPhotoAlbum(payload);
      toast.success(t('page.createPhotoAlbum.created'));
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to create album:', err);
      toast.error(err?.response?.data?.message || t('page.createPhotoAlbum.errors.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} title={<>{t('page.createPhotoAlbum.title')}</>}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-primary">
              {t('page.createPhotoAlbum.coverPhoto')}
            </label>
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 overflow-hidden rounded-lg bg-primary-300 bg-opacity-20">
                {coverPreview ? (
                  <img src={coverPreview} alt="Cover preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <FaCamera className="h-6 w-6 text-primary" />
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <label className="cursor-pointer rounded-lg bg-primary px-3 py-2 text-sm font-medium text-secondary">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  {t('page.createPhotoAlbum.changeCover')}
                </label>
                {coverPreview && (
                  <Button variant="danger" onClick={handleRemoveImage} className="text-sm">
                    {t('general.delete')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="album-title" className="mb-1 block text-sm font-medium text-primary">
              {t('general.title')}
            </label>
            <input
              id="album-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors({ ...errors, title: '' });
              }}
              placeholder={t('page.createPhotoAlbum.titlePlaceholder')}
              className={classNames(
                'w-full rounded-lg border-none !bg-secondary-50 p-2 placeholder:text-primary',
                errors.title && 'ring-2 ring-red-500',
              )}
            />
            {errors.title && <p className="mt-1 text-sm text-error">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="album-description" className="mb-1 block text-sm font-medium text-primary">
              {t('general.description')}
            </label>
            <textarea
              id="album-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('page.createPhotoAlbum.descriptionPlaceholder')}
              rows={4}
              className="w-full resize-none rounded-lg border-none p-2"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-primary">
              {t('page.createPhotoAlbum.uploadSettings')}
            </label>
            <ToggleSwitch
              leftLabel={t('component.modal.editAlbum.uploadSettingRestricted')}
              rightLabel={t('component.modal.editAlbum.uploadSettingOpen')}
              value={openToUpload}
              onChange={(v) => setOpenToUpload(v)}
            />
            <p className="mt-2 text-sm text-gray-600">{t('page.createPhotoAlbum.uploadSettingsDescription')}</p>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)} className="px-6 py-2">
            {t('general.cancel')}
          </Button>
          <Button type="button" variant="primary" disabled={loading} onClick={handleCreate} className="px-6 py-2">
            {loading ? t('page.createPhotoAlbum.creating') : t('page.createPhotoAlbum.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
